import type { TabsProps } from "antd";
import { Tabs, Card } from "antd";
import { UserOutlined } from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";

// Import types
import { TabKey } from "./types";

// Import tab components
import {
  AdminTab,
  // FabricationTab,
  InsuranceTab,
  PayrollTab,
  PermissionsTab,
  // ServicesTab,
  StaffTab,
  StorageTab,
  VendorsTab,
  VesselsTab,
  WebhooksTab,
  UserManagementTab,
  BusinessAddressesTab,
  QuickBooksTab,
} from "./tabs";
import { AddOnServicesTab } from "./tabs/add-on-services-tab-api";

export const BackendSetup: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabKey>(
    (searchParams.get("tab") as TabKey) || "staff-positions"
  );

  // Update URL when tab changes
  const handleTabChange = (key: string) => {
    setActiveTab(key as TabKey);
    setSearchParams({ tab: key });
  };

  // Sync URL params with tab state
  useEffect(() => {
    const tabParam = searchParams.get("tab") as TabKey;
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [activeTab, searchParams]);

  // Updated staff data with base hourly cost instead of employee cost
  // const [staffRoles, setStaffRoles] = useState<StaffRole[]>([
  //   { id: "1", name: "Art Handler", baseHourlyCost: 20, rate: 35 },
  //   { id: "2", name: "Project Manager", baseHourlyCost: 25, rate: 45 },
  //   { id: "3", name: "Driver", baseHourlyCost: 18, rate: 30 },
  // ]);

  // Updated services with more options and unit selection
  // const [services, setServices] = useState<Service[]>([
  //   { id: "1", name: "Reception", rate: 1.25, minimumFee: 75, unit: "cu ft" },
  //   { id: "2", name: "Release", rate: 1.25, minimumFee: 75, unit: "cu ft" },
  //   {
  //     id: "3",
  //     name: "Warehouse Handling",
  //     rate: 2.0,
  //     minimumFee: 100,
  //     unit: "cu ft",
  //   },
  //   { id: "4", name: "Crossdock", rate: 50, minimumFee: 100, unit: "hourly" },
  //   {
  //     id: "5",
  //     name: "Rubbish Removal",
  //     rate: 35,
  //     minimumFee: 70,
  //     unit: "hourly",
  //   },
  //   {
  //     id: "6",
  //     name: "Inventory Handling",
  //     rate: 1.5,
  //     minimumFee: 75,
  //     unit: "cu ft",
  //   },
  // ]);

  // const [fabricationItems, setFabricationItems] = useState<FabricationItem[]>([
  //   {
  //     id: "1",
  //     name: "Simple Ply Box",
  //     rateType: "sq_ft",
  //     rate: 35.0,
  //     minimumCharge: 150,
  //   },
  //   {
  //     id: "2",
  //     name: "Travel Frame",
  //     rateType: "sq_ft",
  //     rate: 45.0,
  //     minimumCharge: 200,
  //   },
  //   {
  //     id: "3",
  //     name: "Design Service",
  //     rateType: "hour",
  //     rate: 85.0,
  //     minimumCharge: 170,
  //   },
  // ]);

  // Only show if current user is SUPER_ADMIN
  if (currentUser?.role !== Role.SUPER_ADMIN) {
    return (
      <Card>
        <div className="text-center py-8">
          <UserOutlined className="text-4xl text-gray-400 mb-4" />
          <h4 className="text-gray-500 text-lg font-medium">
            You don't have permission to access this page
          </h4>
        </div>
      </Card>
    );
  }

  const tabItems: TabsProps["items"] = [
    {
      key: "staff-positions",
      label: "Staff Positions",
      children: <StaffTab />,
    },
    {
      key: "vessels",
      label: "Vessels",
      children: <VesselsTab />,
    },
    {
      key: "storage",
      label: "Storage",
      children: <StorageTab />,
    },
    // {
    //   key: "services",
    //   label: "Services",
    //   children: (
    //     <ServicesTab services={services} onServicesChange={setServices} />
    //   ),
    // },
    {
      key: "admin",
      label: "Admin Charges",
      children: <AdminTab />,
    },
    {
      key: "insurance",
      label: "Insurance",
      children: <InsuranceTab />,
    },
    // {
    //   key: "fabrication",
    //   label: "Fabrication",
    //   children: (
    //     <FabricationTab
    //       fabricationItems={fabricationItems}
    //       onFabricationItemsChange={setFabricationItems}
    //     />
    //   ),
    // },
    {
      key: "vendors",
      label: "3PL Vendors",
      children: <VendorsTab />,
    },
    {
      key: "business-addresses",
      label: "Business Setup",
      children: <BusinessAddressesTab />,
    },
    {
      key: "payroll",
      label: "Payroll & Employees",
      children: <PayrollTab />,
    },
    {
      key: "add-on-services",
      label: "Add-on Services",
      children: <AddOnServicesTab />,
    },
    {
      key: "permissions",
      label: "Permissions",
      children: <PermissionsTab />,
    },
    {
      key: "webhooks",
      label: "Webhooks",
      children: <WebhooksTab />,
    },
    {
      key: "user-management",
      label: "User Management",
      children: <UserManagementTab />,
    },
    {
      key: "quickbooks",
      label: "QuickBooks",
      children: <QuickBooksTab />,
    },
  ];

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h2 className="text-2xl font-medium mb-6">Backend Setup</h2>

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        type="card"
        className="mb-6"
      />

      {/* Form buttons */}
      {/* <div className="flex justify-end space-x-3 mt-6">
        <Button size="large">Cancel</Button>
        <Button size="large" type="default">
          Reset to Defaults
        </Button>
        <Button size="large" type="primary">
          Save Settings
        </Button>
      </div> */}
    </div>
  );
};

export default BackendSetup;
